import React from "react";
import { RiUser3Fill } from "react-icons/ri";

// `position` is the TRUE centre in canvas pixels, projected by LiveCanvas at
// render time. This marker centres itself with `translate(-50%, -50%)`, so it
// consumes that centre directly and subtracts NO glyph half-size (unlike
// StaticStation/StaticSymbol, which position by a plain translate and must
// each subtract their own).
// `showLabel` is decided by LiveCanvas from utils/labelCollision.js: a name
// that would land on another name or another marker is suppressed so the
// remaining ones stay readable. It is only ever the LABEL that is hidden --
// the marker itself always renders -- and hovering (or selecting) the marker
// forces the name back, which is what `onHoverChange` is for.
const PersonnelMarker = ({
  person,
  position,
  isSelected,
  showLabel = true,
  ghost = false,
  onHoverChange,
  onPersonnelClick,
}) => {
  const statusColors = {
    active: "#52c41a",
    warning: "#faad14",
    sos: "#ff4d4f",
    offline: "#8c8c8c",
  };
  const color = statusColors[person.status] || statusColors.offline;

  return (
    <div
      style={{
        position: "absolute",
        left: position.x,
        top: position.y,
        transform: "translate(-50%, -50%)",
        pointerEvents: "auto",
        cursor: "pointer",
        // History replay ghosting only: a subject shown at a last-known
        // position because they are absent from the current playhead bucket
        // (see HistoryWorkspaceModal/utils/buildCanvasPersonnel.js). The live
        // dashboard never sets this, so its markers are unaffected.
        opacity: ghost ? 0.35 : 1,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onPersonnelClick(person.id);
      }}
      onMouseEnter={() => onHoverChange && onHoverChange(person.id)}
      onMouseLeave={() => onHoverChange && onHoverChange(null)}
    >
      {isSelected && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 40,
            height: 40,
            border: `3px dashed ${color}`,
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />
      )}

      {person.status === "sos" && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 30,
            height: 30,
            border: `2px solid ${color}`,
            borderRadius: "50%",
            opacity: 0.6,
            pointerEvents: "none",
            animation: "pulse 1s ease-in-out infinite",
          }}
        />
      )}

      <RiUser3Fill size={20} style={{ color, display: "block" }} />

      {showLabel && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: 4,
            padding: "2px 6px",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            color: color,
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 3,
            whiteSpace: "nowrap",
            boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
            pointerEvents: "none",
          }}
        >
          {person.name}
        </div>
      )}

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              width: 30px;
              height: 30px;
              opacity: 0.6;
            }
            50% {
              width: 40px;
              height: 40px;
              opacity: 0.3;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PersonnelMarker;
